import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Users,
  Briefcase,
  FileSignature,
  Sparkles,
  ListTodo,
  ArrowUpRight,
  Activity,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
} from "recharts";
import { Link } from "react-router-dom";
import StrategicAlerts from "@/components/StrategicAlerts";

const COLORS = ["#D4AF37", "#00F0FF", "#22C55E", "#F4C430", "#A855F7", "#EF4444", "#94A3B8"];

const fmtMoney = (n) => {
  if (!n) return "GH₵ 0";
  if (n >= 1_000_000) return `GH₵ ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `GH₵ ${(n / 1_000).toFixed(0)}K`;
  return `GH₵ ${n}`;
};

const KpiTile = ({ eyebrow, value, label, icon: Icon, delta, accent = false }) => (
  <motion.div
    whileHover={{ y: -4 }}
    transition={{ type: "spring", stiffness: 200, damping: 20 }}
    className="nexus-card aurora-hover p-6 relative overflow-hidden group hover:border-[#D4AF37]/30"
    data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    {accent && (
      <div className="absolute top-0 left-0 w-full h-px"
           style={{ background: "linear-gradient(90deg, transparent, #D4AF37 50%, transparent)" }} />
    )}
    <div className="flex items-start justify-between relative z-10">
      <div className="label-eyebrow">{eyebrow}</div>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/5 bg-white/[0.02] group-hover:border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/5 transition-all">
        <Icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.6} />
      </div>
    </div>
    <div className="mt-5 font-display text-3xl font-medium text-white tracking-tight relative z-10">{value}</div>
    <div className="mt-1 text-xs text-slate-400 relative z-10">{label}</div>
    {delta != null && (
      <div className="mt-3 flex items-center gap-1 text-[11px] font-mono relative z-10">
        <ArrowUpRight className="w-3 h-3" style={{ color: "var(--gold-400)" }} />
        <span style={{ color: "var(--gold-400)" }}>{delta}</span>
      </div>
    )}
  </motion.div>
);

const stageOrder = ["lead", "qualified", "engaged", "negotiation", "active", "dormant"];

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/metrics")
      .then((r) => setMetrics(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-slate-400 font-mono">Synthesizing intelligence…</div>;
  if (!metrics) return <div className="p-10 text-slate-400">Failed to load metrics.</div>;

  const k = metrics.kpis;
  const pipelineData = stageOrder.map((s) => ({
    stage: s.toUpperCase(),
    value: metrics.pipeline_by_stage[s] || 0,
  }));
  const categoryData = Object.entries(metrics.category_counts).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow mb-3">// Executive Command Center</div>
          <h1 className="font-display text-5xl font-medium tracking-tight">
            <span className="text-white">Institutional</span>{" "}
            <span className="text-gradient-gold">Intelligence</span>
          </h1>
          <p className="text-slate-400 mt-3 text-sm max-w-2xl leading-relaxed">
            One Platform. Total Institutional Intelligence. Real-time view across stakeholders, operations and capital pipeline.
          </p>
        </div>
        <div className="hidden md:block font-mono text-[11px] text-slate-500 tracking-widest text-right">
          <div className="flex items-center justify-end gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 glow-pulse" style={{ animationDuration: "2.4s" }}></span>
            LIVE // {new Date().toLocaleTimeString("en-US", { hour12: false })}
          </div>
          <div className="mt-1">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile eyebrow="001 // PIPELINE" value={fmtMoney(k.pipeline_value)} label="Qualified + Engaged + Negotiation" icon={TrendingUp} accent delta="institutional capital under conversation" />
        <KpiTile eyebrow="002 // STAKEHOLDERS" value={k.stakeholders} label="Active relationships" icon={Users} />
        <KpiTile eyebrow="003 // ENGAGEMENTS" value={k.active_projects} label="Live mandates" icon={Briefcase} />
        <KpiTile eyebrow="004 // PROPOSALS" value={fmtMoney(k.proposal_value)} label={`${k.proposals} documents in flight`} icon={FileSignature} />
      </div>

      {/* Strategic Alerts — Workflow Intelligence */}
      <StrategicAlerts />

      {/* Mid Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Pipeline by stage */}
        <div className="nexus-card p-6 lg:col-span-8" data-testid="pipeline-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-eyebrow mb-1">// Capital Pipeline</div>
              <h3 className="font-display text-xl text-white">Value by engagement stage</h3>
            </div>
            <Link to="/crm" className="text-xs font-mono text-slate-400 hover:text-[#D4AF37] transition-colors tracking-widest flex items-center gap-1">
              VIEW CRM <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={pipelineData}>
                <XAxis dataKey="stage" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={fmtMoney} />
                <Tooltip
                  cursor={{ fill: "rgba(212,175,55,0.05)" }}
                  contentStyle={{
                    background: "#0A111F",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    fontFamily: "IBM Plex Mono",
                    fontSize: 12,
                  }}
                  formatter={(v) => fmtMoney(v)}
                />
                <Bar dataKey="value" fill="#D4AF37" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Categories */}
        <div className="nexus-card p-6 lg:col-span-4" data-testid="category-chart">
          <div className="label-eyebrow mb-1">// Composition</div>
          <h3 className="font-display text-xl text-white mb-4">Stakeholder mix</h3>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%" minHeight={140}>
              <PieChart>
                <Pie
                  data={categoryData}
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {categoryData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#0A111F",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    fontFamily: "IBM Plex Mono",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1.5">
            {categoryData.map((c, i) => (
              <div key={c.name} className="flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2" style={{ background: COLORS[i % COLORS.length] }}></span>
                  <span className="text-slate-400 uppercase tracking-widest">{c.name}</span>
                </div>
                <span className="text-white">{c.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Activity */}
        <div className="nexus-card p-6 lg:col-span-5" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow mb-1">// Activity Stream</div>
              <h3 className="font-display text-xl text-white">Recent operations</h3>
            </div>
            <Activity className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
          </div>
          <div className="space-y-2.5 max-h-80 overflow-y-auto">
            {metrics.recent_activity.slice(0, 12).map((a) => (
              <div key={a.id} className="flex items-start gap-3 py-2 border-b border-white/5">
                <div className="w-1.5 h-1.5 mt-2 shrink-0" style={{ background: "var(--gold-500)" }}></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-mono">{a.action}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.entity} {a.meta?.title || a.meta?.name || ""}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-slate-500 shrink-0">
                  {new Date(a.timestamp).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent proposals */}
        <div className="nexus-card p-6 lg:col-span-7" data-testid="recent-proposals">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow mb-1">// Document Engine</div>
              <h3 className="font-display text-xl text-white">In-flight proposals</h3>
            </div>
            <Link to="/proposals" className="text-xs font-mono text-slate-400 hover:text-[#D4AF37] tracking-widest flex items-center gap-1">
              ALL DOCS <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {metrics.recent_proposals.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-3 border border-white/5 hover:border-[#D4AF37]/30 transition-colors">
                <FileSignature className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5 uppercase tracking-widest">
                    {p.type.replace(/_/g, " ")} · v{p.version}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm text-white">{fmtMoney(p.value)}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono mt-0.5"
                    style={{
                      color: p.status === "signed" ? "#22C55E" : p.status === "sent" ? "#3B82F6" : p.status === "review" ? "#F59E0B" : "#94A3B8",
                    }}
                  >
                    {p.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI banner */}
      <Link
        to="/ai"
        data-testid="ai-cta"
        className="block nexus-card p-6 group hover:border-[#D4AF37]/50 transition-all"
        style={{ background: "linear-gradient(110deg, var(--bg-surface-1) 0%, rgba(212,175,55,0.04) 100%)" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37]/40" style={{ background: "rgba(212,175,55,0.08)" }}>
              <Sparkles className="w-5 h-5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
            </div>
            <div>
              <div className="label-eyebrow mb-1">// Nexus AI</div>
              <h3 className="font-display text-xl text-white">Brief your AI consigliere</h3>
              <p className="text-sm text-slate-400 mt-1">
                Ten specialist agents: executive advisor, proposal generator, government strategist, investor advisor, and more.
              </p>
            </div>
          </div>
          <div className="font-mono text-xs tracking-widest text-slate-400 group-hover:text-[#D4AF37] transition-colors flex items-center gap-2">
            ENTER COMMAND <ArrowUpRight className="w-4 h-4" />
          </div>
        </div>
      </Link>
    </div>
  );
}
